import React, { useMemo, useState } from "react";
import { Collapse } from "@mui/material";
import classNames from "classnames";

import InputRow from "components/Input/Row";
import Input from "components/Input";
import Button from "components/Button";
import Dialog, { CloseDialog } from "components/Dialog";
import HelpIcon from "assets/images/icons/JSX/Help";

import { Stat } from "features/Model/Review/Results/General";

import {
    DEFAULT_DEMONSTRATED_LOAD_SETTINGS,
    MAX_PROPOSED_LOADS,
    PROPOSED_LOAD_PRESETS,
} from "utils/utilityData/demonstratedLoad";

import classes from "./style.module.scss";
import resultsClasses from "features/Model/Review/Results/style.module.scss";

const EMPTY_PROPOSED_LOADS = {};

const DemonstratedLoadResults = ({ lastComputed }) => {
    if (lastComputed?.peakDemandAmps == null) return null;

    const peakTimestamp =
        lastComputed.peakHourStart &&
        new Date(lastComputed.peakHourStart).toLocaleString(undefined, {
            dateStyle: "medium",
            timeStyle: "short",
        });

    return (
        <div className={classes.resultsSection}>
            <div className={classNames(resultsClasses.totals, classes.statsGrid)}>
                <Stat
                    label="Peak demand (actual)"
                    value={lastComputed.peakHourAmpsUndiversified}
                    unit="A"
                    decimals={1}
                />
                <Stat label="Peak demand (diversified)" value={lastComputed.peakDemandAmps} unit="A" decimals={1} />
                <Stat
                    label="Effective service limit"
                    value={lastComputed.effectiveServiceLimitAmps}
                    unit="A"
                    decimals={0}
                />
                <Stat label="Headroom" value={lastComputed.headroomAmps} unit="A" decimals={1} />
                {lastComputed.totalProposedAmps > 0 && (
                    <Stat label="Proposed loads" value={lastComputed.totalProposedAmps} unit="A" decimals={1} />
                )}
                <Stat label="Peak hour" value={lastComputed.peakHourKwh} unit="kWh" decimals={2} />
            </div>
            {peakTimestamp && (
                <p className={classes.peakDetail}>
                    Peak hour: {peakTimestamp} · {lastComputed.hoursAnalyzed} hours analyzed · diversity{" "}
                    {(lastComputed.diversityFactor * 100).toFixed(0)}%.
                </p>
            )}
            {lastComputed.warnings?.length > 0 && (
                <ul className={classes.warnings}>
                    {lastComputed.warnings.map((warning) => (
                        <li key={warning}>{warning}</li>
                    ))}
                </ul>
            )}
        </div>
    );
};

const DemonstratedLoadSettingsPanel = ({ demonstratedLoad = {}, onUpdateSettings }) => {
    const [advancedOpen, setAdvancedOpen] = useState(false);
    const [esaDialogOpen, setEsaDialogOpen] = useState(false);

    const settings = { ...DEFAULT_DEMONSTRATED_LOAD_SETTINGS, ...(demonstratedLoad.settings || {}) };

    return (
        <div className={classes.panel}>
            <div className={classes.headerRow}>
                <div>
                    <h4>Demonstrated Load Analysis</h4>
                    <p>
                        Estimate peak electrical demand from hourly utility data using diversity on the single peak
                        hour.
                    </p>
                </div>
                <button
                    type="button"
                    className={classes.helpButton}
                    onClick={() => setEsaDialogOpen(true)}
                    aria-label="ESA Bulletin 8-3-16 information"
                >
                    <HelpIcon />
                </button>
            </div>

            <InputRow style={{ alignItems: "flex-end" }} gridTemplate="1fr 1fr">
                <Input
                    label="Service limit (A)"
                    type="number"
                    input={{
                        value: settings.serviceLimitAmps,
                        onChange: (value) => onUpdateSettings?.("serviceLimitAmps", parseFloat(value) || 0),
                    }}
                />
                <Input
                    label="Service voltage (V)"
                    type="number"
                    input={{
                        value: settings.serviceVoltage,
                        onChange: (value) => onUpdateSettings?.("serviceVoltage", parseFloat(value) || 240),
                    }}
                />
            </InputRow>

            <button type="button" className={classes.advancedToggle} onClick={() => setAdvancedOpen(!advancedOpen)}>
                {advancedOpen ? "Hide" : "Show"} advanced settings
            </button>
            <Collapse in={advancedOpen} timeout="auto" unmountOnExit>
                <InputRow style={{ alignItems: "flex-end" }} gridTemplate="1fr 1fr 1fr">
                    <Input
                        label="Peak-hour diversity factor"
                        info="Applied only to the single peak hour (default 1.25 = 125%)"
                        type="number"
                        input={{
                            value: settings.diversityFactor,
                            onChange: (value) => onUpdateSettings?.("diversityFactor", parseFloat(value) || 1.25),
                        }}
                    />
                    <Input
                        label="Conductor ampacity (A)"
                        info="Optional — uses the lower of service limit and conductor ampacity"
                        type="number"
                        input={{
                            value: settings.conductorAmpacityAmps ?? "",
                            onChange: (value) =>
                                onUpdateSettings?.(
                                    "conductorAmpacityAmps",
                                    value === "" ? null : parseFloat(value) || 0
                                ),
                        }}
                    />
                    <Input
                        label="Analysis period (months)"
                        type="number"
                        input={{
                            value: settings.analysisMonths,
                            onChange: (value) => onUpdateSettings?.("analysisMonths", parseFloat(value) || 12),
                        }}
                    />
                </InputRow>
            </Collapse>

            <Dialog open={esaDialogOpen} closeActions={() => setEsaDialogOpen(false)}>
                <div className={classes.infoDialog}>
                    <CloseDialog closeActions={() => setEsaDialogOpen(false)} />
                    <h2>ESA Bulletin 8-3-16 — Maximum circuit loading and demand factors</h2>
                    <p>
                        Where a dwelling unit has 12 months or more of hourly electrical consumption data, the maximum
                        demand may be calculated from the highest single hour of consumption rather than summing all
                        fixed loads at nameplate values.
                    </p>
                    <div className={classes.formula}>
                        Peak demand (A) = (peak hourly kWh × diversity factor × 1000) ÷ service voltage
                    </div>
                    <p>The diversity factor (typically 125%) is applied to the peak hour in the dataset.</p>
                    <p>
                        <strong>Example:</strong> 9.99 kWh peak hour × 1.25 diversity × 1000 ÷ 240 V = 52.04 A
                        demonstrated peak demand against a 100 A service.
                    </p>
                    <p>Headroom = effective service limit − demonstrated peak demand − proposed additional loads.</p>
                    <p>
                        Reference:{" "}
                        <a
                            href="https://esasafe.com/assets/files/esasafe/pdf/Electrical_Safety_Products/Bulletins/08-03-16.pdf"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            ESA Bulletin 8-3-16
                        </a>
                    </p>
                </div>
            </Dialog>
        </div>
    );
};

const ProposedLoadsSection = ({
    proposedLoads = EMPTY_PROPOSED_LOADS,
    onAddProposedLoad,
    onUpdateProposedLoad,
    onRemoveProposedLoad,
}) => {
    const proposedLoadEntries = useMemo(() => Object.entries(proposedLoads), [proposedLoads]);

    return (
        <div className={classNames(classes.panel, classes.proposedPanelBelow)}>
            <div className={classes.proposedSection}>
                <p>Proposed additional loads (max {MAX_PROPOSED_LOADS})</p>
                <div className={classes.quickAddRow}>
                    {PROPOSED_LOAD_PRESETS.map((preset) => (
                        <Button
                            key={preset.label}
                            type="hollow"
                            smallPadding
                            className={classes.quickAddButton}
                            onClick={() => onAddProposedLoad?.(preset)}
                            disabled={proposedLoadEntries.length >= MAX_PROPOSED_LOADS}
                        >
                            + {preset.label} ({preset.amps} A)
                        </Button>
                    ))}
                </div>
                {proposedLoadEntries.length === 0 ? (
                    <p className={classes.proposedEmpty}>No proposed loads added yet.</p>
                ) : (
                    proposedLoadEntries.map(([key, load]) => (
                        <div key={key} className={classes.proposedLoadRow}>
                            <Input
                                label="Label"
                                type="text"
                                input={{
                                    value: load.label || "",
                                    onChange: (value) => onUpdateProposedLoad?.(key, "label", value),
                                }}
                            />
                            <Input
                                label="Amps"
                                type="number"
                                input={{
                                    value: load.amps ?? "",
                                    onChange: (value) => onUpdateProposedLoad?.(key, "amps", parseFloat(value) || 0),
                                }}
                            />
                            <Button type="hollow" smallPadding onClick={() => onRemoveProposedLoad?.(key)}>
                                Remove
                            </Button>
                        </div>
                    ))
                )}
                {proposedLoadEntries.length < MAX_PROPOSED_LOADS && (
                    <Button
                        type="hollow"
                        smallPadding
                        className={classes.addLoadButton}
                        onClick={() => onAddProposedLoad?.()}
                    >
                        Add proposed load
                    </Button>
                )}
            </div>
        </div>
    );
};

export { DemonstratedLoadResults, DemonstratedLoadSettingsPanel, ProposedLoadsSection };
export default DemonstratedLoadSettingsPanel;
